<template>
  <modal
    :show.sync="showShareReservationsModal"
    modalContentClasses="md"
    modalClasses="modal-alert"
    @close="closeModal()"
    no-close-on-backdrop
  >
    <div class="col-12 pt-4 pb-2">
      <div class="text-center border-bottom pb-3 mb-3">
        <img
          src="~rhomtrip_assets/src/img/icons/alerts/share.png"
          alt="sharer"
          class="icon-modal"
        />
        <h2 class="modal-title mb-1" v-if="reservations.length > 1">
          Share Reservations Links
        </h2>

        <h2 class="modal-title mb-1" v-else>
          Share Reservation Link
        </h2>

        <fade-transition v-if="loadingShare" :duration="300" mode="out-in">
          <div class="bar-preloader-modal">
            <div class="preloader-content">
              <div class="preloader-bar-container">
                <div
                  class="preloader-bar"
                  :style="{ width: progressBarWidth + '%' }"
                ></div>
              </div>
              <p class="preloader-text" data-aos="fade">
                Processing link... Please Wait!
              </p>
            </div>
          </div>
        </fade-transition>
      </div>

      <div class="container-modal-share">
        <div>
          <div class="text-center">
            <h6>For Dispatchers / Organizers</h6>
            <p>
              Dispatchers and organizers get a panel view.
            </p>
            <el-tooltip content="Copy Link (Dispatcher)" placement="bottom">
              <base-button
                class="btn-line-primary "
                @click="generateShareLinkForOrganizer('COPY', reservations)"
                :disabled="loadingShare"
              >
                <i class="flaticon-copy"></i><span>Copy</span>
              </base-button>
            </el-tooltip>

            <el-tooltip
              content="Send via WhatsApp (Dispatcher)"
              placement="bottom"
            >
              <base-button
                class="btn-line-primary "
                @click="generateShareLinkForOrganizer('WHATSAPP', reservations)"
                :disabled="loadingShare"
              >
                <i class="flaticon-web-whatsapp-1"></i>
                <span>Whatsaap</span>
              </base-button>
            </el-tooltip>

            <el-tooltip
              content="Send via Email (Dispatcher)"
              placement="bottom"
            >
              <base-button
                class="btn-line-primary"
                @click="generateShareLinkForOrganizer('EMAIL', reservations)"
                :disabled="loadingShare"
              >
                <i class="flaticon-envelope"> </i>
                <span>Email</span>
              </base-button>
            </el-tooltip>
          </div>

          <div class="text-center" v-if="reservations.length === 1">
            <hr />
            <h6>For Driver</h6>
            <p>
              Drivers get a streamlined web view designed for them.
            </p>
            <el-tooltip content="Copy Link (Driver)" placement="bottom">
              <base-button
                class=" btn-line-primary "
                @click="generateShareLinkForDriver('COPY', reservations)"
                :disabled="loadingShare"
              >
                <i class="flaticon-copy"></i>
                <span>Copy</span>
              </base-button>
            </el-tooltip>

            <el-tooltip content="Send via WhatsApp (Driver)" placement="bottom">
              <base-button
                class="btn-line-primary "
                @click="generateShareLinkForDriver('WHATSAPP', reservations)"
                :disabled="loadingShare"
              >
                <i class="flaticon-web-whatsapp-1"></i>
                <span>Whatsaap</span></base-button
              >
            </el-tooltip>

            <el-tooltip content="Send via Email (Driver)" placement="bottom">
              <base-button
                class="btn-line-primary "
                @click="generateShareLinkForDriver('EMAIL', reservations)"
                :disabled="loadingShare"
              >
                <i class="flaticon-envelope"> </i>
                <span>Email</span></base-button
              >
            </el-tooltip>
          </div>

          <div class="text-center" v-else>
            <hr />
            <h6>For Driver</h6>
            <p>
              Drivers get a streamlined web view designed for them.
            </p>
            <el-tooltip
              content="Download CSV w/Links (Drivers)"
              placement="bottom"
            >
              <base-button
                class=" btn-line-primary "
                @click="generateShareList('DRIVER', reservations)"
                :disabled="loadingShare"
              >
                <i class="flaticon-web-download"></i>
                <span>Download Links</span>
              </base-button>
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>
    <div class="border-top pt-2 text-right col-12 mt-3">
      <base-button
        type="outline-neutral"
        class="min-button"
        @click="closeModal()"
      >
        Close
      </base-button>
    </div>
  </modal>
</template>

<script>
import { FadeTransition } from "vue2-transitions";
import { mapGetters } from "vuex";
import { ReservationsAPI } from "../../../core/api";
import {
  convertToCSV,
  downloadCSV,
  SHARE_LINK_HEADERS
} from "../../../helpers/reservation.helpers";
import { WEBSITE_BASE_URL } from "../../../core/config/env";

export default {
  name: "ShareReservations",
  components: {
    FadeTransition
  },
  props: {
    /**
     * Controls modal visibility.
     * @type {boolean}
     */
    show: {
      type: Boolean,
      default: false
    },
    /**
     * Reservations to share.
     * @type {Array}
     */
    reservations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      /**
       * Modal visibility state.
       * @type {boolean}
       */
      showShareReservationsModal: this.show,
      /**
       * Loading state for progress bar.
       * @type {boolean}
       */
      loadingShare: false,
      /**
       * Interval for progress bar animation.
       * @type {number|null}
       */
      progressInterval: null,
      /**
       * Progress bar percentage.
       * @type {number}
       */
      progressBarWidth: 0
    };
  },
  watch: {
    /**
     * Watch for changes in 'show' prop to update modal visibility.
     * @param {boolean} current
     */
    show: {
      handler(current) {
        this.showShareReservationsModal = current;
      }
    }
  },
  methods: {
    /**
     * Requests backend to create a share link for reservations.
     * @param {Object} payload - Data for link creation.
     * @param {string} payload.type - Link type ("DRIVER" or "ORGANIZER").
     * @param {string} payload.status - Link status ("ACTIVE").
     * @param {Array<string>} payload.reservations - Reservation IDs.
     * @param {Object} payload.metadata - Additional info.
     * @returns {Promise<Object|null>} The generated link object or null if failed.
     */
    async fetchShareReservationsLinks(payload) {
      try {
        let { data } = await ReservationsAPI.createShareReservationsLinks(
          payload
        );
        if (data) {
          return data.data;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    /**
     * Builds the driver share link.
     * @param {string} shareLinkId - Generated link ID.
     * @returns {string} Driver URL.
     */
    buildShareLinkForDriver(shareLinkId) {
      return `${WEBSITE_BASE_URL}/drivers/${shareLinkId}`;
    },
    /**
     * Builds the organizer/dispatcher share link.
     * @param {string} shareLinkId - Generated link ID.
     * @returns {string} Organizer URL.
     */
    buildShareLinkForOrganizer(shareLinkId) {
      return `${WEBSITE_BASE_URL}/dispatchers/${shareLinkId}`;
    },
    /**
     * Builds the share link based on type and reservations.
     * @param {string} type - "DRIVER" or "ORGANIZER".
     * @param {Array<Object>} reservations - Reservations array.
     * @returns {Promise<string|null>} Generated URL or null if failed.
     */
    async buildShareLink(type, reservations = []) {
      const payload = {
        type: type,
        status: "ACTIVE",
        reservations: reservations.map(
          reservation => reservation.reservation._id
        ),
        metadata: {}
      };
      this.startProgressBar();
      const shareLink = await this.fetchShareReservationsLinks(payload);
      this.completeProgressBar();
      if (shareLink) {
        if (type === "DRIVER") {
          return this.buildShareLinkForDriver(shareLink._id);
        } else if (type === "ORGANIZER") {
          return this.buildShareLinkForOrganizer(shareLink._id);
        }
        return null;
      }
      return null;
    },
    /**
     * Generates and shares the link for organizer.
     * @param {string} mode - "WHATSAPP", "EMAIL" or "COPY".
     * @param {Array<Object>} reservations - Reservations array.
     * @returns {Promise<void>}
     */
    async generateShareLinkForOrganizer(mode, reservations = []) {
      try {
        const shareLink = await this.buildShareLink("ORGANIZER", reservations);
        if (shareLink) {
          if (mode === "WHATSAPP") {
            this.shareLinkByWhatsApp(shareLink, reservations.length);
          } else if (mode === "EMAIL") {
            this.shareLinkByEmail(shareLink, reservations.length);
          } else if (mode === "COPY") {
            this.copyReservationLink(shareLink);
          } else {
            this.$notify({
              type: "warning",
              title: "Warning!",
              message: "No reservations selected to share.",
              icon: "flaticon-warning"
            });
          }
        } else {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "An error occurred while generating the share link.",
            icon: "flaticon-remove"
          });
        }
      } catch (error) {
        console.error("Error generating share link for organizer:", error);
        this.$notify({
          type: "danger",
          title: "Error!",
          message: "An error occurred while generating the share link.",
          icon: "flaticon-remove"
        });
      }
    },
    /**
     * Generates and shares the link for driver.
     * @param {string} mode - "WHATSAPP", "EMAIL" or "COPY".
     * @param {Array<Object>} reservations - Reservations array.
     * @returns {Promise<void>}
     */
    async generateShareLinkForDriver(mode, reservations = []) {
      try {
        const shareLink = await this.buildShareLink("DRIVER", reservations);
        if (shareLink) {
          if (mode === "WHATSAPP") {
            this.shareLinkByWhatsApp(shareLink, reservations.length);
          } else if (mode === "EMAIL") {
            this.shareLinkByEmail(shareLink, reservations.length);
          } else if (mode === "COPY") {
            this.copyReservationLink(shareLink);
          } else {
            this.$notify({
              type: "warning",
              title: "Warning!",
              message: "No reservations selected to share.",
              icon: "flaticon-warning"
            });
          }
        } else {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "An error occurred while generating the share link.",
            icon: "flaticon-remove"
          });
        }
      } catch (error) {
        console.error("Error generating share link for driver:", error);
        this.$notify({
          type: "danger",
          title: "Error!",
          message: "An error occurred while generating the share link.",
          icon: "flaticon-remove"
        });
      }
    },
    /**
     * Opens WhatsApp with the generated link.
     * @param {string} shareLink - URL to share.
     * @param {number} reservationsLength - Number of reservations.
     * @returns {void}
     */
    shareLinkByWhatsApp(shareLink, reservationsLength) {
      const whatsappLink = this.generateWhatsAppContent(
        shareLink,
        reservationsLength
      );
      window.open(whatsappLink, "_blank");
      this.$notify({
        type: "success",
        title: "Success!",
        message: "The link has been shared via WhatsApp",
        icon: "flaticon-check-3"
      });
    },
    /**
     * Opens email client with the generated link.
     * @param {string} shareLink - URL to share.
     * @param {number} reservationsLength - Number of reservations.
     * @returns {void}
     */
    shareLinkByEmail(shareLink, reservationsLength) {
      const emailLink = this.generateEmailContent(
        shareLink,
        reservationsLength
      );
      window.open(emailLink, "_blank");
      this.$notify({
        type: "success",
        title: "Success!",
        message: "The link has been shared via Email",
        icon: "flaticon-check-3"
      });
    },
    /**
     * Copies the link to clipboard.
     * @param {string} shareLink - URL to copy.
     * @returns {Promise<void>}
     */
    async copyReservationLink(shareLink) {
      try {
        await navigator.clipboard.writeText(shareLink);
        this.onCopyLink();
      } catch {
        this.onErrorCopyLink();
      }
    },
    /**
     * Generates WhatsApp share content.
     * @param {string} shareLink - URL to share.
     * @param {number} reservationsLength - Number of reservations.
     * @returns {string} WhatsApp URL with message.
     */
    generateWhatsAppContent(shareLink, reservationsLength) {
      const baseMessage =
        reservationsLength > 1
          ? `Hi! We’re sharing this link so you can view the reservations: ${shareLink}`
          : `Hi! We’re sharing this link so you can view the reservation: ${shareLink}`;

      const encoded = encodeURIComponent(baseMessage);
      const whatsappLink = `https://wa.me/?text=${encoded}`;
      return whatsappLink;
    },
    /**
     * Generates email share content.
     * @param {string} shareLink - URL to share.
     * @param {number} reservationsLength - Number of reservations.
     * @returns {string} Email URL with message.
     */
    generateEmailContent(shareLink, reservationsLength) {
      const body =
        reservationsLength > 1
          ? `Hi! We’re sharing this link so you can view the reservations: ${shareLink}`
          : `Hi! We’re sharing this link so you can view the reservation: ${shareLink}`;
      const subject =
        reservationsLength > 1
          ? "Reservation Link – View Reservations Details"
          : "Reservation Link – View Reservation Details";

      const emailLink = `mailto:?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;

      return emailLink;
    },
    /**
     * Notifies success when link is copied.
     * @returns {void}
     */
    onCopyLink() {
      this.$notify({
        type: "success",
        title: "Success!",
        message: "The link has been copied to the clipboard",
        icon: "flaticon-check-3"
      });
    },
    /**
     * Notifies error when link copy fails.
     * @returns {void}
     */
    onErrorCopyLink() {
      this.$notify({
        type: "danger",
        title: "Error!",
        message: "The link couldn't be copied",
        icon: "flaticon-remove"
      });
    },
    /**
     * Starts progress bar animation.
     * @returns {void}
     */
    startProgressBar() {
      this.loadingShare = true;
      this.progressBarWidth = 0;
      clearInterval(this.progressInterval);
      this.progressInterval = setInterval(() => {
        if (this.progressBarWidth < 90) {
          this.progressBarWidth += 1;
        }
      }, 50);
    },
    /**
     * Completes progress bar animation.
     * @returns {void}
     */
    completeProgressBar() {
      this.progressBarWidth = 100;
      setTimeout(() => this.stopProgressBar(), 300);
    },
    /**
     * Stops progress bar animation.
     * @returns {void}
     */
    stopProgressBar() {
      clearInterval(this.progressInterval);
      this.loadingShare = false;
    },
    /**
     * Closes modal and emits event.
     * @returns {void}
     */
    closeModal() {
      this.showShareReservationsModal = false;
      this.$emit("close");
    },
    /**
     * Requests backend to create a share reservations list (CSV).
     * @param {Object} payload - Data for list creation.
     * @param {string} payload.type - List type ("ACTIVE").
     * @param {string} payload.status - List status ("ACTIVE").
     * @param {Array<string>} payload.reservations - Reservation IDs.
     * @param {Object} payload.metadata - Additional info.
     * @returns {Promise<Object|null>} The generated list object or null if failed.
     */
    async fetchShareReservationsList(payload) {
      try {
        let { data } = await ReservationsAPI.createShareReservationsList(
          payload
        );
        if (data) {
          return data.data;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    /**
     * Generates and downloads a CSV share list for the reservations.
     * @param {string} type - List type ("ACTIVE").
     * @param {Array<Object>} reservations - Reservations array.
     * @returns {Promise<void>}
     */
    async generateShareList(type, reservations = []) {
      const payload = {
        type: type,
        status: "ACTIVE",
        reservations: reservations.map(
          reservation => reservation.reservation._id
        ),
        metadata: {}
      };
      this.startProgressBar();
      const data = await this.fetchShareReservationsList(payload);
      this.completeProgressBar();
      if (data) {
        const { sharedList } = data;
        const csvText = convertToCSV(sharedList, SHARE_LINK_HEADERS);
        let url = downloadCSV(
          csvText,
          `share_list_${new Date().toISOString()}.csv`
        );
        if (url) {
          this.$notify({
            type: "success",
            title: "Success!",
            message: "The list has been downloaded successfully",
            icon: "flaticon-check-3"
          });
        } else {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "An error occurred while downloading the share list.",
            icon: "flaticon-remove"
          });
        }
      } else {
        this.$notify({
          type: "danger",
          title: "Error!",
          message: "An error occurred while generating the share list.",
          icon: "flaticon-remove"
        });
      }
    }
  },
  computed: {
    /**
     * User session getter.
     */
    ...mapGetters(["sesion"])
  }
};
</script>
